import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Archive, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react'
import {
  chatKeys,
  flattenInfiniteChats,
  useAgentRuntime,
  useAgentStore,
  useChats,
  useDeleteChat
} from '@multiplayer-app/ai-agent-react'
import { AgentSessionKind, type AgentChat } from '@multiplayer-app/ai-agent-types'
import { getDocumentId, getDocumentIdentity } from '@/shared/office/host'

interface OpenDocument {
  documentId?: string
  documentName?: string
  documentPath?: string
}

const META_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
const UNKNOWN_DOCUMENT = 'Unknown'

function chatDocumentName(chat: AgentChat): string {
  const name = chat.metadata?.documentName
  return typeof name === 'string' && name ? name : UNKNOWN_DOCUMENT
}

function chatMatchesDocument(chat: AgentChat, doc: OpenDocument): boolean {
  const meta = chat.metadata ?? {}
  if (doc.documentId && typeof meta.documentId === 'string') return meta.documentId === doc.documentId
  if (doc.documentPath && typeof meta.documentPath === 'string') return meta.documentPath === doc.documentPath
  // Unsaved docs (and chats predating the stable id) only have a name to match on.
  if (doc.documentName && typeof meta.documentName === 'string') return meta.documentName === doc.documentName
  return false
}

/**
 * A `display: contents` mount inserted as the first child of `selector`, so a
 * portal renders into a library-owned element (the shell / the controls bar)
 * at the native position without disturbing its flex layout.
 */
function usePortalNode(selector: string): HTMLElement | null {
  const [node, setNode] = useState<HTMLElement | null>(null)
  useEffect(() => {
    let mount: HTMLDivElement | undefined
    const attach = (): boolean => {
      const parent = document.querySelector(selector)
      if (!parent) return false
      mount = document.createElement('div')
      mount.style.display = 'contents'
      parent.insertBefore(mount, parent.firstChild)
      setNode(mount)
      return true
    }
    if (attach()) return () => mount?.remove()
    const observer = new MutationObserver(() => {
      if (attach()) observer.disconnect()
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      mount?.remove()
    }
  }, [selector])
  return node
}

/**
 * Document-scoped chat history, rendered into the library's native panel
 * location (and a toggle into its controls bar) via portals, reusing the
 * library's class names so it inherits the native styling. Adds search and
 * filters chats to the open document.
 */
export function DocumentChatHistory() {
  const isOpen = useAgentStore((state) => state.isChatHistoryOpen)
  const setOpen = useAgentStore((state) => state.setChatHistoryOpen)
  const activeChatId = useAgentStore((state) => state.activeChatId)
  const setActiveChat = useAgentStore((state) => state.setActiveChat)
  const runtime = useAgentRuntime()
  const { deleteChat, isDeleting } = useDeleteChat()

  // useDeleteChat updates the store (which the native list reads) and invalidates
  // a key our `useChats` query doesn't use, so refetch the list explicitly.
  const handleDeleteChat = useCallback(
    async (chatId: string) => {
      await deleteChat(chatId)
      void runtime.queryClient.invalidateQueries({ queryKey: chatKeys.all })
    },
    [deleteChat, runtime.queryClient]
  )

  const controlsNode = usePortalNode('.mp-agent-agent-controls-left')
  const shellNode = usePortalNode('.mp-agent-shell')

  const [query, setQuery] = useState('')

  const [doc, setDoc] = useState<OpenDocument>({})
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const [documentId, identity] = await Promise.all([getDocumentId(), getDocumentIdentity()])
      if (!cancelled) setDoc({ documentId, documentName: identity.documentName, documentPath: identity.documentPath })
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useChats({ enabled: isOpen })
  // Drain every page so the client-side filter sees all chats; per-user volume is low.
  useEffect(() => {
    if (isOpen && hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage])

  const items = useMemo(() => {
    const q = query.trim().toLowerCase()
    return flattenInfiniteChats(data)
      .filter((chat) => chat.sessionKind !== AgentSessionKind.SUBAGENT)
      .filter((chat) => chatMatchesDocument(chat, doc))
      .filter(
        (chat) => !q || (chat.title ?? '').toLowerCase().includes(q) || chatDocumentName(chat).toLowerCase().includes(q)
      )
      .sort((a, b) => (a.updatedAt === b.updatedAt ? 0 : a.updatedAt < b.updatedAt ? 1 : -1))
  }, [data, doc, query])

  const renderItem = (chat: AgentChat) => (
    <li
      key={chat.id}
      onClick={() => setActiveChat(chat.id)}
      className={`mp-agent-chat-history-item${chat.id === activeChatId ? ' active' : ''}`}
    >
      <div className='mp-agent-chat-history-item-content'>
        <span className='mp-agent-chat-history-item-title'>{chat.title || 'Untitled chat'}</span>
        <span className='mp-agent-chat-history-item-meta'>
          {new Date(chat.updatedAt).toLocaleString(undefined, META_FORMAT)}
        </span>
      </div>
      <div className='mp-agent-chat-history-item-actions'>
        <button
          type='button'
          className='mp-agent-icon-button mp-agent-icon-button-sm'
          aria-label='Delete chat'
          title='Delete chat'
          disabled={isDeleting(chat.id)}
          onClick={(event) => {
            event.stopPropagation()
            void handleDeleteChat(chat.id)
          }}
        >
          <Archive size={14} />
        </button>
      </div>
    </li>
  )

  const toggle =
    controlsNode &&
    createPortal(
      <button
        type='button'
        className='mp-agent-icon-button mp-agent-icon-button-md'
        onClick={() => setOpen(!isOpen)}
        aria-label={isOpen ? 'Hide chat history' : 'Show chat history'}
        title={isOpen ? 'Hide chat history' : 'Show chat history'}
      >
        {isOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
      </button>,
      controlsNode
    )

  const panel =
    isOpen &&
    shellNode &&
    createPortal(
      <div className='mp-agent-chat-history-panel'>
        <section className='mp-agent-chat-history'>
          <h4>Chats</h4>
          <div className='ramp-history-toolbar'>
            <div className='ramp-history-search'>
              <Search size={14} />
              <input
                type='text'
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder='Search chats'
                aria-label='Search chats'
              />
            </div>
          </div>
          {items.length === 0 ? (
            <div className='mp-agent-chat-history-empty'>
              {query.trim() ? 'No matching chats.' : 'No chats for this document yet.'}
            </div>
          ) : (
            <ul className='mp-agent-chat-history-list'>{items.map(renderItem)}</ul>
          )}
        </section>
      </div>,
      shellNode
    )

  return (
    <>
      {toggle}
      {panel}
    </>
  )
}
